import { taskAPI } from '@/utils/api';
import { buildHistoryTasksListQuery } from './historyTasksListQuery';
import { sanitizeCopywritingContent } from '@/pages/ipe/workbench/constants/aiCopywritingConfig';
import {
  buildGoodsCompositeSelectionId,
  parseGoodsCompositeSelectionId,
} from '@/utils/goodsCompositeSelection';
import { resolveOriginalLineageImageId } from './exportStampRoleMeta';

/**
 * 从 AI 文案正文取可展示的「名称」：优先首行有效文本。
 */
export function pickCopywritingTitleText(content) {
  const cleaned = sanitizeCopywritingContent(String(content || '').trim())
    || String(content || '').trim();
  if (!cleaned) return '';
  const firstLine = cleaned
    .split(/\n+/)
    .map((line) => line.trim())
    .find(Boolean);
  return String(firstLine || cleaned).trim();
}

function splitContentBlocks(content, refCount) {
  const blocks = String(content || '')
    .split(/\n\n+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (blocks.length === 0) return [];
  if (refCount <= 1) return blocks;
  if (blocks.length === refCount) return blocks;
  if (blocks.length === 1) return blocks;
  return blocks;
}

function taskFinishedAtMs(task) {
  const raw = task?.finishedAt || task?.updatedAt || task?.createdAt || '';
  const ms = Date.parse(String(raw));
  return Number.isFinite(ms) ? ms : 0;
}

function isSuccessLikeStatus(status) {
  const s = String(status || '').trim().toLowerCase();
  return s === 'success' || s === 'partial' || s === 'completed' || s === 'done';
}

function toIdList(value) {
  if (value instanceof Map) {
    return [...value.keys()].map(String).filter(Boolean);
  }
  if (Array.isArray(value)) {
    return value.map((id) => String(id || '').trim()).filter(Boolean);
  }
  if (value && typeof value === 'object') {
    return Object.keys(value).map(String).filter(Boolean);
  }
  return [];
}

function readAliasList(aliasIdsByKey, key) {
  if (!aliasIdsByKey) return [];
  const raw = typeof aliasIdsByKey.get === 'function'
    ? aliasIdsByKey.get(key)
    : aliasIdsByKey[key];
  return (Array.isArray(raw) ? raw : [])
    .map((id) => String(id || '').trim())
    .filter(Boolean);
}

/**
 * 展开可用于匹配 AI 文案 imageIds 的别名（原图 / crop_original_ / 自身）。
 * @param {string} imageId
 * @returns {string[]}
 */
export function expandCopywritingMatchIds(imageId) {
  const id = String(imageId || '').trim();
  if (!id) return [];
  const out = new Set([id]);
  const orig = resolveOriginalLineageImageId(id);
  if (orig) {
    out.add(orig);
    out.add(`crop_original_${orig}`);
  }
  if (/^crop_original_/i.test(id)) {
    const bare = id.replace(/^crop_original_/i, '');
    if (bare) out.add(bare);
  }
  return [...out];
}

function buildWantedMatchKeys(wantedId, aliasIdsByKey) {
  const keys = new Set(expandCopywritingMatchIds(wantedId));
  readAliasList(aliasIdsByKey, wantedId).forEach((alias) => {
    expandCopywritingMatchIds(alias).forEach((id) => keys.add(id));
  });
  return keys;
}

function findMatchedTaskImageIndex(taskImageIds, wantedMatchKeys) {
  for (let index = 0; index < taskImageIds.length; index += 1) {
    const tid = String(taskImageIds[index] || '').trim();
    if (!tid) continue;
    for (const alias of expandCopywritingMatchIds(tid)) {
      if (wantedMatchKeys.has(alias)) {
        return index;
      }
    }
  }
  return -1;
}

async function ensureFullCopywritingTask(task) {
  const content = String(task?.result?.content || task?.result?.text || '').trim();
  if (content) return task;
  try {
    const res = await taskAPI.getById(task.id);
    return res?.task || res?.data || res || task;
  } catch {
    return task;
  }
}

async function loadCopywritingCandidateTasks({
  linkedIds = [],
  sourceGoodsTaskId = '',
  allowUnscopedScan = false,
  allowImageIdScopedScan = false,
} = {}) {
  const linkedTasks = [];
  for (const id of linkedIds) {
    try {
      const res = await taskAPI.getById(id);
      const task = res?.task || res?.data || res;
      if (task) linkedTasks.push(task);
    } catch (error) {
      console.warn('[goodsExportPreview] 加载关联 AI 文案任务失败', id, error);
    }
  }

  let scannedTasks = [];
  const shouldScan = linkedIds.length === 0
    && (sourceGoodsTaskId || allowUnscopedScan || allowImageIdScopedScan);
  if (shouldScan) {
    try {
      const res = await taskAPI.getAll(buildHistoryTasksListQuery({}, {
        type: 'aiCopywriting',
        page: 1,
        pageSize: 100,
      }));
      scannedTasks = Array.isArray(res?.tasks) ? res.tasks : [];
    } catch (error) {
      console.warn('[goodsExportPreview] 加载 AI 文案任务失败', error);
    }
  }

  return { linkedTasks, scannedTasks };
}

/**
 * 按任意图片 id（原图 / crop_original_* / crop_* / composite-*）关联 AI 文案标题。
 * 图片 id 作用域扫描：无 linked / sourceGoodsTaskId 时，仍允许按 imageId 血缘交集匹配（不无脑扫全库乱绑）。
 *
 * @param {Iterable<string>} imageIds
 * @param {{
 *   linkedAiCopywritingTaskIds?: Iterable<string>,
 *   sourceGoodsTaskId?: string,
 *   allowUnscopedScan?: boolean,
 *   aliasIdsByImageId?: Map<string, string[]>|Record<string, string[]>,
 * }} [options]
 * @returns {Promise<Map<string, { title: string, language: string }>>}
 */
export async function resolveAiCopywritingTitlesByImageIds(imageIds, options = {}) {
  const wantedList = [...(imageIds || [])]
    .map((id) => String(id || '').trim())
    .filter(Boolean);
  if (wantedList.length === 0) return new Map();

  const wanted = new Set(wantedList);
  const aliasIdsByImageId = options.aliasIdsByImageId || null;
  const matchKeysByWanted = new Map();
  const allMatchKeys = new Set();
  wantedList.forEach((id) => {
    const keys = buildWantedMatchKeys(id, aliasIdsByImageId);
    matchKeysByWanted.set(id, keys);
    keys.forEach((k) => allMatchKeys.add(k));
  });

  const linkedIds = [...(options.linkedAiCopywritingTaskIds || [])]
    .map((id) => String(id || '').trim())
    .filter(Boolean);
  const sourceGoodsTaskId = String(options.sourceGoodsTaskId || '').trim();
  const allowUnscopedScan = options.allowUnscopedScan === true;
  // 有明确图片 id 时允许按交集扫库（比 composite 无作用域安全）
  const allowImageIdScopedScan = options.allowImageIdScopedScan !== false;

  const { linkedTasks, scannedTasks } = await loadCopywritingCandidateTasks({
    linkedIds,
    sourceGoodsTaskId,
    allowUnscopedScan,
    allowImageIdScopedScan,
  });

  if (linkedIds.length === 0 && scannedTasks.length === 0 && linkedTasks.length === 0) {
    return new Map();
  }

  const seen = new Set();
  const pool = linkedIds.length > 0 ? linkedTasks : [...linkedTasks, ...scannedTasks];
  const candidates = pool
    .filter((task) => {
      const id = String(task?.id || '').trim();
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .filter((task) => isSuccessLikeStatus(task?.status))
    .filter((task) => {
      const ids = (Array.isArray(task?.imageIds) ? task.imageIds : []).map(String);
      const hit = ids.some((id) => {
        for (const alias of expandCopywritingMatchIds(id)) {
          if (allMatchKeys.has(alias)) return true;
        }
        return false;
      });
      if (!hit) return false;
      if (linkedIds.length > 0) return true;
      if (sourceGoodsTaskId) {
        const src = String(
          task?.result?.sourceGoodsTaskId
          || task?.sourceGoodsTaskId
          || '',
        ).trim();
        return src === sourceGoodsTaskId;
      }
      return allowUnscopedScan || allowImageIdScopedScan;
    })
    .sort((a, b) => {
      const aLinked = linkedIds.includes(String(a?.id || '')) ? 1 : 0;
      const bLinked = linkedIds.includes(String(b?.id || '')) ? 1 : 0;
      if (aLinked !== bLinked) return bLinked - aLinked;
      return taskFinishedAtMs(b) - taskFinishedAtMs(a);
    });

  const titleByImageId = new Map();

  for (const raw of candidates) {
    if ([...wanted].every((id) => titleByImageId.has(id))) break;
    const task = await ensureFullCopywritingTask(raw);
    const taskImageIds = (Array.isArray(task?.imageIds) ? task.imageIds : []).map(String);
    const content = String(task?.result?.content || task?.result?.text || '').trim();
    if (!content || taskImageIds.length === 0) continue;

    const language = String(
      task?.result?.language
      || task?.result?.targetLanguage
      || '',
    ).trim();
    const blocks = splitContentBlocks(content, taskImageIds.length);

    wantedList.forEach((wantedId) => {
      if (titleByImageId.has(wantedId)) return;
      const matchKeys = matchKeysByWanted.get(wantedId) || new Set();
      const matchedIndex = findMatchedTaskImageIndex(taskImageIds, matchKeys);
      if (matchedIndex < 0) return;
      const block = blocks.length === taskImageIds.length
        ? blocks[matchedIndex]
        : (blocks[0] || content);
      const title = pickCopywritingTitleText(block);
      if (title) titleByImageId.set(wantedId, { title, language });
    });
  }

  return titleByImageId;
}

/**
 * 按成品勾选 ID（composite-{form}-{group}-{result}）关联 AI 文案任务结果。
 * AI 文案任务创建时把 composite id 写入 imageIds，正文在 result.content。
 *
 * 隔离规则（composite-* 跨任务不唯一，禁止无作用域扫全库）：
 * 1. 有 linkedAiCopywritingTaskIds → 只使用这些任务
 * 2. 否则若提供 sourceGoodsTaskId → 只匹配 result.sourceGoodsTaskId 相同的文案任务
 * 3. 否则不关联标题（避免把无关任务的文案拼进来）
 * 4. 若提供 aliasIdsByCompositeId（印花/原图血缘），composite 未命中时再按图片 id 作用域补标题
 *
 * @param {Iterable<string>} compositeIds
 * @param {{
 *   linkedAiCopywritingTaskIds?: Iterable<string>,
 *   sourceGoodsTaskId?: string,
 *   allowUnscopedScan?: boolean,
 *   aliasIdsByCompositeId?: Map<string, string[]>|Record<string, string[]>,
 * }} [options]
 * @returns {Promise<Map<string, { title: string, language: string }>>} compositeId -> 标题与生成语种
 */
export async function resolveAiCopywritingTitlesByCompositeIds(compositeIds, options = {}) {
  const wanted = new Set(
    [...(compositeIds || [])]
      .map((id) => String(id || '').trim())
      .filter((id) => Boolean(parseGoodsCompositeSelectionId(id))),
  );
  if (wanted.size === 0) return new Map();

  const linkedIds = [...(options.linkedAiCopywritingTaskIds || [])]
    .map((id) => String(id || '').trim())
    .filter(Boolean);
  const sourceGoodsTaskId = String(options.sourceGoodsTaskId || '').trim();
  const allowUnscopedScan = options.allowUnscopedScan === true;
  const aliasIdsByCompositeId = options.aliasIdsByCompositeId || null;

  const { linkedTasks, scannedTasks } = await loadCopywritingCandidateTasks({
    linkedIds,
    sourceGoodsTaskId,
    allowUnscopedScan,
  });

  const titleByCompositeId = new Map();

  if (linkedIds.length > 0 || scannedTasks.length > 0) {
    const seen = new Set();
    const pool = linkedIds.length > 0 ? linkedTasks : scannedTasks;
    const candidates = pool
      .filter((task) => {
        const id = String(task?.id || '').trim();
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .filter((task) => isSuccessLikeStatus(task?.status))
      .filter((task) => {
        const ids = (Array.isArray(task?.imageIds) ? task.imageIds : []).map(String);
        if (!ids.some((id) => wanted.has(id))) return false;
        if (linkedIds.length > 0) return true;
        if (sourceGoodsTaskId) {
          const src = String(
            task?.result?.sourceGoodsTaskId
            || task?.sourceGoodsTaskId
            || '',
          ).trim();
          return src === sourceGoodsTaskId;
        }
        return allowUnscopedScan;
      })
      .sort((a, b) => {
        const aLinked = linkedIds.includes(String(a?.id || '')) ? 1 : 0;
        const bLinked = linkedIds.includes(String(b?.id || '')) ? 1 : 0;
        if (aLinked !== bLinked) return bLinked - aLinked;
        return taskFinishedAtMs(b) - taskFinishedAtMs(a);
      });

    for (const raw of candidates) {
      if ([...wanted].every((id) => titleByCompositeId.has(id))) break;
      const task = await ensureFullCopywritingTask(raw);
      const imageIds = (Array.isArray(task?.imageIds) ? task.imageIds : []).map(String);
      const content = String(task?.result?.content || task?.result?.text || '').trim();
      if (!content || imageIds.length === 0) continue;

      const language = String(
        task?.result?.language
        || task?.result?.targetLanguage
        || '',
      ).trim();

      const blocks = splitContentBlocks(content, imageIds.length);
      imageIds.forEach((id, index) => {
        if (!wanted.has(id) || titleByCompositeId.has(id)) return;
        const block =
          blocks.length === imageIds.length
            ? blocks[index]
            : blocks[0] || content;
        const title = pickCopywritingTitleText(block);
        if (title) titleByCompositeId.set(id, { title, language });
      });
    }
  }

  // composite 未命中时：用印花/原图血缘按图片 id 作用域补标题（原图做 AI 文案的常见场景）
  const missing = [...wanted].filter((id) => !titleByCompositeId.has(id));
  if (missing.length > 0 && aliasIdsByCompositeId) {
    const hasAnyAlias = missing.some((id) => readAliasList(aliasIdsByCompositeId, id).length > 0);
    if (hasAnyAlias) {
      const byImage = await resolveAiCopywritingTitlesByImageIds(missing, {
        linkedAiCopywritingTaskIds: linkedIds,
        sourceGoodsTaskId,
        allowUnscopedScan,
        aliasIdsByImageId: aliasIdsByCompositeId,
        allowImageIdScopedScan: true,
      });
      missing.forEach((id) => {
        const meta = byImage.get(id);
        if (meta?.title) titleByCompositeId.set(id, meta);
      });
    }
  }

  return titleByCompositeId;
}

/**
 * 从本次导出涉及的成品勾选 ID 列表。
 */
export function collectCompositeIdsForExportPreview(goodsForms, selectedImages) {
  const selectedSet =
    selectedImages instanceof Set ? selectedImages : new Set(selectedImages || []);
  if (selectedSet.size > 0) {
    return [...selectedSet].map(String).filter((id) => Boolean(parseGoodsCompositeSelectionId(id)));
  }

  const ids = [];
  (goodsForms || []).forEach((form, formIndex) => {
    const groupCount = form?.imageGroups?.length || 0;
    for (let groupIndex = 0; groupIndex < groupCount; groupIndex += 1) {
      const results = form?.compositeResults?.[groupIndex] || [];
      // 调用方会再按 dedupe 解析；这里用粗数量兜底索引
      const count = Array.isArray(results) ? results.length : 0;
      for (let resultIndex = 0; resultIndex < count; resultIndex += 1) {
        ids.push(buildGoodsCompositeSelectionId(formIndex, groupIndex, resultIndex));
      }
    }
  });
  return ids;
}

export { toIdList };
